import { useState } from "react";
import DialogHT from "../../components/DialogHT";
import Staff from "../../components/Staff";
import { StickyNote, Plus, MessageSquare, Calendar } from "lucide-react";
import { usePrimaryColor } from "../../components/PrimaryColorContext";


/**
 * ClassNotes Component
 * Displays class notes in compact horizontal card.
 *
 * @component
 * @param {Object} props - Component props
 * @param {Object} props.htClass - The class object containing notes
 * @param {Function} props.setHtClass - Function to update the class object
 *
 * @author Forge
 * @date 2026-04-10
 * @version 3.0.0
 */
export default function ClassNotes({ htClass, setHtClass }) {
  const [open, setOpen] = useState(false);
  const { primaryColor } = usePrimaryColor();

  return (
    <div className="flex-1 min-w-[280px] bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden hover:shadow-md transition-shadow">
      {/* Header */}
      <div className="px-4 py-3 border-b border-slate-100 bg-slate-50/50 flex justify-between items-center">
        <h3 className="text-base font-black text-slate-900 flex items-center gap-2 uppercase tracking-tight">
          <StickyNote size={16} className={`text-${primaryColor}-800`} /> Notes
        </h3>
        <button
          className="bg-white hover:bg-slate-50 border border-slate-200 text-slate-600 p-1.5 rounded-lg shadow-sm transition-all active:scale-95 cursor-pointer"
          onClick={() => setOpen(true)}
        >
          <Plus size={14} strokeWidth={3} />
        </button>
      </div>

      {/* Notes List */}
      <div className="p-3 overflow-y-auto max-h-[280px]">
        <div className="flex flex-col gap-2">
          {htClass.notes && htClass.notes.length > 0 ? (
            htClass.notes.slice(0, 5).map((note) => (
              <div
                key={note._id}
                className={`flex flex-col gap-1.5 p-3 bg-slate-50/50 rounded-xl border border-slate-100 hover:border-${primaryColor}-50 hover:bg-${primaryColor}-50 transition-all relative overflow-hidden`}
              >
                <div className={`absolute top-0 left-0 w-1 h-full bg-${primaryColor}-50 group-hover:bg-${primaryColor}-800 transition-colors`} />

                <div className="text-xs text-slate-700 leading-relaxed font-medium pl-1">
                  {note.note}
                </div>

                <div className="flex items-center justify-between pt-1.5 border-t border-slate-100/50 mt-1">
                  <div className="flex items-center gap-1.5">
                    <div className="w-4 h-4 rounded-md bg-white flex items-center justify-center text-slate-400">
                      <MessageSquare size={10} />
                    </div>
                    <div className="text-[0.6rem] font-bold text-slate-500">
                      <Staff id={note.recordedBy} />
                    </div>
                  </div>
                  <div className="flex items-center gap-1 text-[0.55rem] font-black text-slate-400 uppercase tracking-tighter">
                    <Calendar size={10} />
                    {new Date(note.createdAt).fDate()}
                  </div>
                </div>
              </div>
            ))
          ) : (
            <div className="flex flex-col items-center justify-center py-8 text-slate-300 gap-2">
              <StickyNote size={32} strokeWidth={1} opacity={0.5} />
              <div className="text-xs font-black uppercase tracking-wider">
                No notes yet
              </div>
            </div>
          )}
        </div>
      </div>

      <DialogHT
        open={open}
        setOpen={setOpen}
        content={content}
        title="addANewNote"
        url={`/add-new-class-note/${htClass._id}`}
        success={(notes) => setHtClass((prev) => ({ ...prev, notes }))}
      />
    </div>
  );
}

const content = [
  {
    name: "note",
    label: "note",
    multiline: true,
    rows: 4,
  },
];